// Student Editor - PartDPage3 (Learner Reflection - Editable, Large View)
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useActivityContext } from '../index';
import { API_CONFIG } from '../../../../../config/api';

const PartDPage3 = () => {
  const { activity } = useActivityContext();
  const [formData, setFormData] = useState({
    awareness: {
      item_1_checked: false,
      item_2_checked: false,
      item_3_checked: false
    },
    sensitivity: {
      item_1_checked: false,
      item_2_checked: false,
      item_3_checked: false
    },
    creativity: {
      item_1_checked: false,
      item_2_checked: false,
      item_3_checked: false
    },
    self_reflection: ''
  });
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const saveTimeoutRef = useRef(null);

  useEffect(() => {
    if (activity?.my_response_part_d?.page_3) {
      const page3Data = activity.my_response_part_d.page_3;
      setFormData({
        awareness: page3Data.awareness || { item_1_checked: false, item_2_checked: false, item_3_checked: false },
        sensitivity: page3Data.sensitivity || { item_1_checked: false, item_2_checked: false, item_3_checked: false },
        creativity: page3Data.creativity || { item_1_checked: false, item_2_checked: false, item_3_checked: false },
        self_reflection: page3Data.self_reflection || ''
      });
    }
  }, [activity]);

  const saveChanges = async (dataToSave) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('accessToken');
      const apiUrl = API_CONFIG.BASE_URL;

      await axios.put(
        `${apiUrl}/secondary/user/domain-activities/${activity.activity_id}/part-d/page/3/student-fields`,
        dataToSave,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSaveMessage('Saved ✓');
      setTimeout(() => setSaveMessage(''), 2000);
    } catch (error) {
      console.error('Error saving:', error);
      setSaveMessage('Error saving');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleCheckboxChange = (category, itemKey) => {
    const updated = {
      ...formData[category],
      [itemKey]: !formData[category][itemKey]
    };
    setFormData({ ...formData, [category]: updated });
    saveChanges({ [category]: updated });
  };

  const handleSelfReflectionChange = (value) => {
    setFormData({ ...formData, self_reflection: value });

    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => saveChanges({ self_reflection: value }), 1000);
  };

  const getTotalChecked = (category) => {
    const data = formData[category];
    return [data.item_1_checked, data.item_2_checked, data.item_3_checked].filter(Boolean).length;
  };

  const renderAssessmentCategory = (category, title, items) => {
    const categoryData = formData[category];
    const totalChecked = getTotalChecked(category);

    return (
      <div style={{ marginBottom: '25px' }}>
        {/* Category Header */}
        <div style={{
          backgroundColor: '#87CEEB',
          color: '#000',
          padding: '15px',
          fontSize: '16px',
          fontWeight: 'bold',
          textAlign: 'center',
          marginBottom: '15px',
          borderRadius: '4px'
        }}>
          {title}
        </div>

        {/* Content Area */}
        <div style={{
          backgroundColor: '#E6F3FF',
          padding: '20px',
          borderRadius: '4px'
        }}>
          {/* Checkbox Items */}
          {items.map((text, idx) => {
            const itemNum = idx + 1;
            const itemKey = `item_${itemNum}_checked`;

            return (
              <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '15px', marginBottom: '15px' }}>
                <input
                  type="checkbox"
                  checked={categoryData[itemKey]}
                  onChange={() => handleCheckboxChange(category, itemKey)}
                  style={{
                    width: '20px',
                    height: '20px',
                    cursor: 'pointer',
                    minWidth: '20px',
                    marginTop: '3px'
                  }}
                />
                <div style={{ flex: 1, fontSize: '14px', lineHeight: '1.6' }}>
                  {text}
                </div>
              </div>
            );
          })}

          {/* Total Count */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', marginTop: '20px' }}>
            <span style={{ fontSize: '14px', fontWeight: 'bold' }}>Total number of (✓):</span>
            <div style={{
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              border: '4px solid #4A7BA7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              fontWeight: 'bold',
              backgroundColor: 'white'
            }}>
              {totalChecked}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '30px',
      backgroundColor: 'white',
      fontFamily: "'Poppins', Arial, sans-serif"
    }}>
      {/* Save indicator */}
      {saveMessage && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: saving ? '#FFC107' : '#4CAF50',
          color: saving ? 'black' : 'white',
          padding: '12px 24px',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          zIndex: 1000,
          fontSize: '16px',
          fontWeight: 'bold'
        }}>
          {saveMessage}
        </div>
      )}

      {/* Header */}
      <div style={{
        fontSize: '20px',
        fontWeight: 'bold',
        textAlign: 'center',
        backgroundColor: '#4A7BA7',
        color: 'white',
        padding: '15px',
        marginBottom: '25px',
        borderRadius: '4px'
      }}>
        Learner Reflection
      </div>

      {/* Instruction */}
      <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '20px' }}>
        Tick (✓) all statements that apply.
      </div>

      {/* Three Assessment Categories */}
      {renderAssessmentCategory('awareness', 'Awareness', [
        'I was sufficiently prepared for the activity.',
        'I demonstrated a solid understanding of the key concepts covered.',
        'I changed my approach as per the changing dynamics of the activity.'
      ])}

      {renderAssessmentCategory('sensitivity', 'Sensitivity', [
        'I actively considered different perspectives during the activity.',
        'I acknowledged the feelings and opinions of my peers.',
        'I gave constructive feedback in a considerate manner.'
      ])}

      {renderAssessmentCategory('creativity', 'Creativity', [
        'I approached challenges with a willingness to explore unique solutions.',
        'I demonstrated flexibility in my thinking.',
        'I actively sought inspiration from different sources to improve my contribution.'
      ])}

      {/* Self-Reflection Section */}
      <div style={{ marginTop: '30px' }}>
        <div style={{ marginBottom: '15px', display: 'flex', gap: '20px', alignItems: 'center' }}>
          {/* Stick Figure */}
          <svg width="60" height="85" viewBox="0 0 50 70">
            <circle cx="25" cy="15" r="12" fill="none" stroke="#000" strokeWidth="2" />
            <line x1="25" y1="27" x2="25" y2="45" stroke="#000" strokeWidth="2" />
            <line x1="25" y1="45" x2="15" y2="65" stroke="#000" strokeWidth="2" />
            <line x1="25" y1="45" x2="35" y2="65" stroke="#000" strokeWidth="2" />
            <line x1="25" y1="32" x2="10" y2="40" stroke="#000" strokeWidth="2" />
            <line x1="25" y1="32" x2="40" y2="40" stroke="#000" strokeWidth="2" />
          </svg>

          {/* Speech Bubble */}
          <div style={{
            border: '3px solid #000',
            borderRadius: '20px',
            padding: '15px 20px',
            backgroundColor: 'white',
            fontSize: '15px',
            fontWeight: 'bold',
            textAlign: 'center',
            maxWidth: '280px'
          }}>
            Leave some words of appreciation/encouragement for yourself.
          </div>

          {/* Arrow */}
          <svg width="40" height="25" viewBox="0 0 30 20">
            <path d="M 0 10 L 25 10 M 25 10 L 20 5 M 25 10 L 20 15" stroke="#000" strokeWidth="2.5" fill="none" />
          </svg>
        </div>

        {/* Self-Reflection Textarea */}
        <textarea
          value={formData.self_reflection}
          onChange={(e) => handleSelfReflectionChange(e.target.value)}
          placeholder="Write your self-reflection here..."
          maxLength={2000}
          style={{
            width: '100%',
            backgroundColor: '#E6F3FF',
            padding: '20px',
            fontSize: '14px',
            minHeight: '150px',
            border: '2px solid #87CEEB',
            borderRadius: '8px',
            fontFamily: "'Poppins', Arial, sans-serif",
            resize: 'vertical',
            lineHeight: '1.6'
          }}
        />
      </div>
    </div>
  );
};

export default PartDPage3;
